import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../../shared/components/PageHeader';
import CompanyPeriodSelector from '../../../shared/components/CompanyPeriodSelector';
import { exportToExcel, exportToPdf } from './incomeStatementExports';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import './IncomeStatementPage.css';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE_URL = baseEnvironment.replace(/\/$/, '');

const UI_TEXT = {
  'tr-TR': {
    forbidden: 'Bu sirket icin gelir tablosu verilerine erisim yetkiniz yok.',
    fetchFailed: 'Gelir tablosu verisi alinamadi.',
    fetchError: 'Gelir tablosu yuklenirken hata olustu.',
    companyNotSelected: 'Sirket secilmedi',
    periodNotSelected: 'Donem secilmedi',
    headerDescription: '{{company}} | {{period}} icin TDHP uyumlu gelir tablosu ozeti.',
    title: 'Gelir Tablosu Intelligence',
    hideZero: 'Sifir satirlari gizle',
    showZero: 'Sifir satirlari goster',
    refreshing: 'Yenileniyor...',
    refresh: 'Guncelle',
    exportExcelTitle: 'Excel olarak indir',
    exportPdfTitle: 'PDF olarak indir',
    infoMessage: 'Sirket ve donem seciminden sonra gelir tablosu verileri yuklenir.',
    loadingMessage: 'Gelir tablosu verileri hazirlaniyor...',
    errorTitle: 'Gelir tablosu verisi alinamadi',
    errorHint: 'Olasi nedenler: arka uc calismiyor, secilen donem verisi yok veya yetkiniz kisitli.',
    emptyTitle: 'Secilen sirket/donemde gelir tablosu verisi bulunamadi.',
    emptyHint: 'Excel import edildi mi? Donemi yeniden secin veya verileri yenileyin.',
    grossSales: 'Brut Satislar',
    netSales: 'Net Satislar',
    costOfSales: 'Satilan Malin Maliyeti',
    grossProfit: 'Brut Kar',
    lineCount: 'Satir sayisi',
    netPeriodProfit: 'Net donem kari',
    zeroRows: 'Sifir satirlar',
    hidden: 'Gizli',
    visible: 'Gosteriliyor',
    accountDesc: 'Hesap / Aciklama',
    amountTl: 'Tutar (TL)',
    tlSuffix: 'TL'
  },
  'en-US': {
    forbidden: 'You do not have permission to access income statement data for this company.',
    fetchFailed: 'Income statement data could not be fetched.',
    fetchError: 'An error occurred while loading income statement.',
    companyNotSelected: 'Company not selected',
    periodNotSelected: 'Period not selected',
    headerDescription: 'TDHP-compatible income statement summary for {{company}} | {{period}}.',
    title: 'Income Statement Intelligence',
    hideZero: 'Hide zero lines',
    showZero: 'Show zero lines',
    refreshing: 'Refreshing...',
    refresh: 'Refresh',
    exportExcelTitle: 'Download as Excel',
    exportPdfTitle: 'Download as PDF',
    infoMessage: 'Income statement data will be loaded after selecting company and period.',
    loadingMessage: 'Preparing income statement data...',
    errorTitle: 'Income statement data could not be fetched',
    errorHint: 'Possible reasons: backend is down, selected period has no data, or your access is restricted.',
    emptyTitle: 'No income statement data found for selected company/period.',
    emptyHint: 'Was Excel imported? Re-select the period or refresh data.',
    grossSales: 'Gross Sales',
    netSales: 'Net Sales',
    costOfSales: 'Cost of Goods Sold',
    grossProfit: 'Gross Profit',
    lineCount: 'Line count',
    netPeriodProfit: 'Net period profit',
    zeroRows: 'Zero rows',
    hidden: 'Hidden',
    visible: 'Visible',
    accountDesc: 'Account / Description',
    amountTl: 'Amount (TRY)',
    tlSuffix: 'TRY'
  }
};

const resolveLocaleKey = (value) => (String(value || '').toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR');

const buildHeaders = () => {
  const token = sessionStorage.getItem('authToken');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const parseErrorMessage = async (response, fallback, t) => {
  if (response.status === 403) return t('forbidden');
  try {
    const payload = await response.json();
    if (payload?.message) return payload.message;
  } catch {
    // ignore
  }
  try {
    const text = await response.text();
    if (text) return text;
  } catch {
    // ignore
  }
  return fallback;
};

const findAmountByCode = (lines, code) => Number(lines.find((row) => row.accountCode === code)?.amount ?? 0);

function IncomeStatementPage() {
  const { language } = useLanguage();
  const localeKey = resolveLocaleKey(language);
  const text = UI_TEXT[localeKey];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);

  const currencyFormatter = useMemo(() => new Intl.NumberFormat(localeKey, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }), [localeKey]);

  const formatCurrency = useCallback((value) => {
    const suffix = t('tlSuffix');
    if (value === null || value === undefined) return `${currencyFormatter.format(0)} ${suffix}`;
    const numberValue = Number(value);
    if (Number.isNaN(numberValue)) return `${currencyFormatter.format(0)} ${suffix}`;
    return `${currencyFormatter.format(numberValue)} ${suffix}`;
  }, [currencyFormatter, t]);

  const [selection, setSelection] = useState({ companyId: '', periodId: '', company: null, period: null });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasData, setHasData] = useState(false);
  const [hideZeroLines, setHideZeroLines] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchData = useCallback(async () => {
    if (!selection.companyId || !selection.periodId) {
      setRows([]);
      setHasData(false);
      setError('');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({ companyId: selection.companyId, periodId: selection.periodId });
      const response = await fetch(`${API_BASE_URL}/api/financials/income-statement?${params}`, { headers: buildHeaders() });
      if (!response.ok) {
        const errorMessage = await parseErrorMessage(response, t('fetchFailed'), t);
        throw new Error(errorMessage);
      }
      const payload = await response.json().catch(() => ({}));
      const statement = Array.isArray(payload.incomeStatement) ? payload.incomeStatement : [];
      setRows(statement);
      setHasData(Boolean(payload.hasData));
    } catch (caught) {
      setError(caught?.message || t('fetchError'));
      setRows([]);
      setHasData(false);
    } finally {
      setLoading(false);
    }
  }, [selection.companyId, selection.periodId, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const toggleZeroLines = () => setHideZeroLines((prev) => !prev);
  const handleRefresh = () => setRefreshKey((prev) => prev + 1);

  const exportFilename = useMemo(() => {
    const company = selection.company?.name?.replace(/[^a-zA-Z0-9\u00C0-\u024F]/g, '_') || 'company';
    const period = selection.period?.code || selection.period?.description?.replace(/[^a-zA-Z0-9]/g, '_') || 'period';
    return `income_statement_${company}_${period}`;
  }, [selection.company, selection.period]);

  const companyLabel = selection.company?.name || t('companyNotSelected');
  const periodLabel = selection.period?.code || selection.period?.description || t('periodNotSelected');
  const headerDescription = t('headerDescription').replace('{{company}}', companyLabel).replace('{{period}}', periodLabel);

  const handleExportExcel = () => {
    if (rows.length) exportToExcel(rows, exportFilename, hideZeroLines);
  };

  const handleExportPdf = () => {
    if (rows.length) exportToPdf(rows, exportFilename, headerDescription, hideZeroLines);
  };

  const visibleRows = useMemo(() => rows.filter((row) => {
    if (hideZeroLines && row.lineType === 'DETAIL') {
      const amount = Number(row.amount ?? 0);
      if (Math.abs(amount) < 0.01) return false;
    }
    return true;
  }), [rows, hideZeroLines]);

  const netProfitAmount = findAmountByCode(rows, 'NET_DONEM_CALC');
  const grossSalesAmount = findAmountByCode(rows, 'A_TOTAL');
  const netSalesAmount = findAmountByCode(rows, 'C_TOTAL');
  const costOfSalesAmount = findAmountByCode(rows, 'D_TOTAL');
  const grossProfitAmount = findAmountByCode(rows, 'BRUT_KAR_TOTAL');

  const netProfitFormatted = formatCurrency(netProfitAmount);
  const readyToShowTable = rows.length > 0 && hasData;

  const renderRows = visibleRows.map((row, index) => {
    const rowClass = `income-statement__row income-statement__row--${row.lineType?.toLowerCase() ?? 'detail'}`;
    return (
      <tr key={`${row.section}-${row.accountCode}-${index}`} className={rowClass}>
        <td className="income-statement__account">
          <span className="income-statement__code">{row.accountCode}</span>
          <span className="income-statement__name">{row.accountName}</span>
          <span className="income-statement__category">{row.category}</span>
        </td>
        <td className="income-statement__amount">{formatCurrency(row.amount ?? 0)}</td>
      </tr>
    );
  });

  return (
    <div className="income-statement">
      <PageHeader title={t('title')} description={headerDescription} />

      <CompanyPeriodSelector onSelectionChange={setSelection} />

      <div className="income-statement__controls">
        <button type="button" className={`btn-secondary income-statement__toggle ${hideZeroLines ? 'active' : ''}`} onClick={toggleZeroLines}>
          {hideZeroLines ? t('showZero') : t('hideZero')}
        </button>
        <button type="button" className="btn-primary" onClick={handleRefresh} disabled={loading || !selection.companyId || !selection.periodId}>
          {loading ? t('refreshing') : t('refresh')}
        </button>
        <button type="button" className="btn-secondary" onClick={handleExportExcel} disabled={!readyToShowTable || !rows.length} title={t('exportExcelTitle')}>Excel</button>
        <button type="button" className="btn-secondary" onClick={handleExportPdf} disabled={!readyToShowTable || !rows.length} title={t('exportPdfTitle')}>PDF</button>
      </div>

      {(!selection.companyId || !selection.periodId) ? (
        <div className="income-statement__alert income-statement__alert--info"><p>{t('infoMessage')}</p></div>
      ) : loading ? (
        <div className="income-statement__alert income-statement__alert--loading"><p>{t('loadingMessage')}</p></div>
      ) : error ? (
        <div className="income-statement__alert income-statement__alert--error">
          <p>{t('errorTitle')}</p>
          <p className="income-statement__alert-detail">{error}</p>
          <p className="income-statement__alert-detail">{t('errorHint')}</p>
        </div>
      ) : !readyToShowTable ? (
        <div className="income-statement__alert income-statement__alert--empty">
          <p>{t('emptyTitle')}</p>
          <p className="income-statement__alert-detail">{t('emptyHint')}</p>
        </div>
      ) : (
        <>
          <div className="income-statement__stats income-statement__stats--summary">
            <div className="income-statement__stat-card income-statement__stat-card--sales"><p>{t('grossSales')}</p><strong>{formatCurrency(grossSalesAmount)}</strong></div>
            <div className="income-statement__stat-card income-statement__stat-card--sales"><p>{t('netSales')}</p><strong>{formatCurrency(netSalesAmount)}</strong></div>
            <div className="income-statement__stat-card income-statement__stat-card--cost"><p>{t('costOfSales')}</p><strong>{formatCurrency(costOfSalesAmount)}</strong></div>
            <div className="income-statement__stat-card income-statement__stat-card--profit"><p>{t('grossProfit')}</p><strong>{formatCurrency(grossProfitAmount)}</strong></div>
          </div>

          <div className="income-statement__stats income-statement__stats--meta">
            <div className="income-statement__stat-card"><p>{t('lineCount')}</p><strong>{visibleRows.length}</strong></div>
            <div className="income-statement__stat-card"><p>{t('netPeriodProfit')}</p><strong>{netProfitFormatted}</strong></div>
            <div className="income-statement__stat-card"><p>{t('zeroRows')}</p><strong>{hideZeroLines ? t('hidden') : t('visible')}</strong></div>
          </div>

          <div className="income-statement__table-wrapper">
            <table className="income-statement__table">
              <thead>
                <tr>
                  <th>{t('accountDesc')}</th>
                  <th className="income-statement__amount-header">{t('amountTl')}</th>
                </tr>
              </thead>
              <tbody>{renderRows}</tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default IncomeStatementPage;
